/* eslint-disable prettier/prettier */
import React from 'react';
import styles from '../../../../styles/components/pageComponents/coach/ResourceManager.module.css';
import Tooltip from '../../../../components/common/ToolTip';
// --- Interfaces ---
export interface ResourceItem {
	isActive?: boolean | undefined;
	id: string;
	name: string;
	size?: string;
	type?: string;
	dueDate?: string;
	isFolder?: boolean;
	status?: 'idle' | 'uploading' | 'completed' | 'failed';
	progress?: number; // 0 to 100
	visibility?: boolean;
	downloadable?: boolean;
	editingRowId?: string | null;
	isUploading?: boolean;
}

interface ColumnConfig {
	header: string;
	render: (item: ResourceItem) => React.ReactNode;
}

interface ResourceManagerProps {
	title?: string;
	breadcrumb?: string[];
	columns?: ColumnConfig[];
	data: ResourceItem[];
	onAddFolder?: () => void;
	onAddResource?: () => void;
	/**
	 * Called when user requests to delete an item that has already been persisted.
	 */
	onDelete?: (id: string) => void;
	/**
	 * Called when user removes an item that is still pending upload or has failed.
	 * ResourceManager will remove it from view immediately and skip calling onDelete.
	 */
	onRemoveLocal?: (id: string) => void;
	onEdit?: (id: string) => void;
	onRowClick?: (item: ResourceItem) => void;

	onBack?: () => void;
	primaryActionText?: string;
	editingRowId?: string | null;
	onSecondaryAction?: () => void;
	secondaryActionText?: string;
}

const ResourceManager: React.FC<ResourceManagerProps> = ({
	title,
	breadcrumb = [],
	columns = [],
	data = [],
	onAddFolder,
	onAddResource,
	onDelete,
	onRemoveLocal,
	onEdit,
	onBack,
	editingRowId,
	onRowClick,
	onSecondaryAction,
	secondaryActionText = 'View Coachee Uploads',

	primaryActionText = 'Add File',
}) => {
	// Filter folders to display them separately on mobile
	const folders = data.filter(item => item.isFolder);
	const files = data.filter(item => !item.isFolder);

	return (
		<div className={styles.container}>
			<div className={styles.header}>
				<h3 className={styles.title}>{title}</h3>

				{/* ACTION BUTTONS IN HEADER */}
				<div className={styles.headerActionGroup}>
					{onSecondaryAction && (
						<button
							className={styles.secondaryHeaderBtn}
							onClick={onSecondaryAction}
						>
							{secondaryActionText}
						</button>
					)}

					{onAddResource && (
						<button
							className={styles.primaryHeaderBtn}
							onClick={onAddResource}
						>
							{primaryActionText}{' '}
							<span className={styles.plus}>+</span>
						</button>
					)}
				</div>
			</div>

			<nav className={styles.breadcrumb}>
				{breadcrumb.map((item, i) => (
					<span key={i}>
						{item} {i < breadcrumb.length - 1 && ' > '}
					</span>
				))}
			</nav>

			{/* MOBILE ONLY FOLDER SECTION */}
			<div className={styles.mobileFolderSection}>
				{folders.map(folder => (
					<div
						key={folder.id}
						className={styles.mobileFolderCard}
						onClick={() => onRowClick?.(folder)}
					>
						<span className={styles.icon}>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="30"
								height="30"
								viewBox="0 0 30 30"
								fill="none"
							>
								<path
									d="M23 23.5C23.5304 23.5 24.0391 23.2893 24.4142 22.9142C24.7893 22.5391 25 22.0304 25 21.5V11.5C25 10.9696 24.7893 10.4609 24.4142 10.0858C24.0391 9.71071 23.5304 9.5 23 9.5H15.1C14.7655 9.50328 14.4355 9.42261 14.1403 9.26538C13.8451 9.10815 13.594 8.87938 13.41 8.6L12.6 7.4C12.4179 7.12347 12.17 6.89648 11.8785 6.7394C11.587 6.58231 11.2611 6.50005 10.93 6.5H7C6.46957 6.5 5.96086 6.71071 5.58579 7.08579C5.21071 7.46086 5 7.96957 5 8.5V21.5C5 22.0304 5.21071 22.5391 5.58579 22.9142C5.96086 23.2893 6.46957 23.5 7 23.5H23Z"
									stroke="#1E1E1E"
									stroke-width="1.5"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
							</svg>
						</span>
						<span className={styles.folderName}>{folder.name}</span>
					</div>
				))}
			</div>

			<div className={styles.tableWrapper}>
				<table className={styles.table}>
					<thead className={styles.tableHeader}>
						<tr>
							<th>Files</th>
							{columns.map((col, i) => (
								<th key={i}>{col.header}</th>
							))}
							<th style={{ textAlign: 'right' }}>Actions</th>
						</tr>
					</thead>
					<tbody>
						{/* Folder Navigation Row */}
						{onBack && (
							<tr className={styles.navRow} onClick={onBack}>
								{/* Added fileNameCell class here so it remains visible on mobile */}
								<td
									colSpan={columns.length + 2}
									className={styles.fileNameCell}
								>
									<Tooltip text="Go to Previous Folder">
										<span
											className={styles.backIcon}
											style={{
												fontSize: '20px',
												marginRight: '8px',
											}}
										>
											<svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
												<path d="M5.4375 0.75L0.75 5.4375L5.4375 10.125M0.75 5.4375H10.5938C11.2709 5.4375 11.9414 5.57087 12.567 5.83C13.1925 6.08912 13.761 6.46893 14.2398 6.94773C14.7186 7.42653 15.0984 7.99495 15.3575 8.62054C15.6166 9.24612 15.75 9.91662 15.75 10.5938C15.75 11.2709 15.6166 11.9414 15.3575 12.567C15.0984 13.1925 14.7186 13.761 14.2398 14.2398C13.761 14.7186 13.1925 15.0984 12.567 15.3575C11.9414 15.6166 11.2709 15.75 10.5938 15.75H7.3125" stroke="#1E1E1E" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
											</svg>
										</span>
									</Tooltip>
									<span style={{ fontWeight: 600 }}>
										Previous Folder
									</span>
								</td>
							</tr>
						)}

						{data.map(item => (
							<tr
								key={item.id}
								onClick={() =>
									item.isFolder && onRowClick?.(item)
								}
								style={{
									cursor: item.isFolder
										? 'pointer'
										: 'default',
								}}
								className={`${item.isFolder ? styles.folderRow : ''} \
                  ${item.status === 'uploading' ? styles.uploadingRow : ''} \
                  ${item.status === 'failed' ? styles.failedRow : ''} \
                  ${item.status === 'completed' ? styles.completedRow : ''}`}
							>
								<td className={styles.fileNameCell}>
									<span className={styles.icon}>
										{item.isFolder ? (
											<Tooltip text="Open Folder">
												{' '}
												<svg
													xmlns="http://www.w3.org/2000/svg"
													width="30"
													height="30"
													viewBox="0 0 30 30"
													fill="none"
												>
													<path
														d="M23 23.5C23.5304 23.5 24.0391 23.2893 24.4142 22.9142C24.7893 22.5391 25 22.0304 25 21.5V11.5C25 10.9696 24.7893 10.4609 24.4142 10.0858C24.0391 9.71071 23.5304 9.5 23 9.5H15.1C14.7655 9.50328 14.4355 9.42261 14.1403 9.26538C13.8451 9.10815 13.594 8.87938 13.41 8.6L12.6 7.4C12.4179 7.12347 12.17 6.89648 11.8785 6.7394C11.587 6.58231 11.2611 6.50005 10.93 6.5H7C6.46957 6.5 5.96086 6.71071 5.58579 7.08579C5.21071 7.46086 5 7.96957 5 8.5V21.5C5 22.0304 5.21071 22.5391 5.58579 22.9142C5.96086 23.2893 6.46957 23.5 7 23.5H23Z"
														stroke="#1E1E1E"
														stroke-width="1.5"
														stroke-linecap="round"
														stroke-linejoin="round"
													/>
												</svg>{' '}
											</Tooltip>
										) : (
											''
										)}
									</span>
									<div className={styles.nameContainer}>
										<span
											className={
												item.status === 'uploading'
													? styles.uploadingName
													: ''
											}
										>
											{item.name}
											{item.status === 'completed' && (
												<span
													className={
														styles.statusIcon
													}
													title="Uploaded"
												>
													✔
												</span>
											)}
											{item.status === 'failed' && (
												<span
													className={
														styles.statusIconError
													}
													title="Upload failed"
												>
													✖
												</span>
											)}
										</span>
										{item.status === 'uploading' && (
											<div
												className={
													styles.progressContainer
												}
											>
												<div
													className={
														styles.progressBar
													}
													style={{
														width: `${item.progress || 0}%`,
													}}
												/>
											</div>
										)}
									</div>
								</td>

								{columns.map((col, i) => (
									<td key={i}>{col.render(item)}</td>
								))}

								<td className={styles.actionsCell}>
									<div className={styles.actionGroup}>
										{!item.isFolder && (
											<>
												{/* 🔥 MOBILE UPLOAD PROGRESS REPLACES ICONS */}
												{item.isUploading ? (
													<div
														className={
															styles.mobileUploadProgress
														}
													>
														<div
															className={
																styles.mobileProgressBar
															}
														>
															<div
																className={
																	styles.mobileProgressFill
																}
																style={{
																	width: `${item.progress || 0}%`,
																}}
															/>
														</div>
														<span
															className={
																styles.mobileProgressText
															}
														>
															{item.progress || 0}
															%
														</span>
													</div>
												) : (
													<>
														{onEdit && (
															<Tooltip
																text={
																	item.id ===
																		editingRowId
																		? 'Save Changes'
																		: 'Edit Item'
																}
															>
																<button
																	onClick={e => {
																		e.stopPropagation();
																		onEdit(
																			item.id
																		);
																	}}
																	className={
																		styles.actionBtn
																	}
																>
																	{item.id ===
																		editingRowId ? (
																		/* 🔥 SAVE ICON */
																		<svg
																			xmlns="http://www.w3.org/2000/svg"
																			width="40"
																			height="40"
																			viewBox="0 0 40 40"
																			fill="none"
																		>
																			<rect
																				x="0.5"
																				y="0.5"
																				width="39"
																				height="39"
																				rx="4.5"
																				fill="white"
																			/>
																			<rect
																				x="0.5"
																				y="0.5"
																				width="39"
																				height="39"
																				rx="4.5"
																				stroke="#D9D9D9"
																			/>
																			<path
																				d="M25.5556 30V22.2222C25.5556 21.9275 25.4385 21.6449 25.2301 21.4365C25.0217 21.2282 24.7391 21.1111 24.4444 21.1111H15.5556C15.2609 21.1111 14.9783 21.2282 14.7699 21.4365C14.5615 21.6449 14.4444 21.9275 14.4444 22.2222V30M14.4444 10V14.4444C14.4444 14.7391 14.5615 15.0217 14.7699 15.2301C14.9783 15.4385 15.2609 15.5556 15.5556 15.5556H23.3333M23.5556 10C24.1417 10.0083 24.7008 10.248 25.1111 10.6667L29.3333 14.8889C29.752 15.2992 29.9917 15.8583 30 16.4444V27.7778C30 28.3671 29.7659 28.9324 29.3491 29.3491C28.9324 29.7659 28.3671 30 27.7778 30H12.2222C11.6329 30 11.0676 29.7659 10.6509 29.3491C10.2341 28.9324 10 28.3671 10 27.7778V12.2222C10 11.6329 10.2341 11.0676 10.6509 10.6509C11.0676 10.2341 11.6329 10 12.2222 10H23.5556Z"
																				stroke="#1E1E1E"
																				strokeWidth="1.5"
																				strokeLinecap="round"
																				strokeLinejoin="round"
																			/>
																		</svg>
																	) : (
																		/* 🔥 EDIT ICON */
																		<svg
																			xmlns="http://www.w3.org/2000/svg"
																			width="40"
																			height="40"
																			viewBox="0 0 40 40"
																			fill="none"
																		>
																			<rect
																				x="0.5"
																				y="0.5"
																				width="39"
																				height="39"
																				rx="4.5"
																				fill="white"
																			/>
																			<rect
																				x="0.5"
																				y="0.5"
																				width="39"
																				height="39"
																				rx="4.5"
																				stroke="#D9D9D9"
																			/>
																			<path
																				d="M22.9999 13.0004L26.9999 17.0004M29.1739 14.8124C29.7026 14.2838 29.9997 13.5668 29.9998 12.8192C29.9999 12.0716 29.703 11.3546 29.1744 10.8259C28.6459 10.2972 27.9289 10.0001 27.1813 10C26.4337 9.99991 25.7166 10.2968 25.1879 10.8254L11.8419 24.1744C11.6098 24.4059 11.438 24.6909 11.3419 25.0044L10.0209 29.3564C9.99509 29.4429 9.99314 29.5347 10.0153 29.6222C10.0374 29.7097 10.0829 29.7896 10.1467 29.8534C10.2106 29.9172 10.2906 29.9624 10.3781 29.9845C10.4656 30.0065 10.5575 30.0044 10.6439 29.9784L14.9969 28.6584C15.3101 28.5631 15.5951 28.3925 15.8269 28.1614L29.1739 14.8124Z"
																				stroke="#1E1E1E"
																				strokeWidth="1.5"
																				strokeLinecap="round"
																				strokeLinejoin="round"
																			/>
																		</svg>
																	)}
																</button>
															</Tooltip>
														)}

														{onDelete && (
															<Tooltip text="Delete Item">
																<button
																	onClick={e => {
																		e.stopPropagation();
																		// if the item isn't a persisted resource (uploading/failed),
																		// perform a local removal instead of invoking an API.
																		if (
																			item.status &&
																			item.status !==
																			'completed'
																		) {
																			onRemoveLocal?.(
																				item.id
																			);
																		} else {
																			onDelete?.(
																				item.id
																			);
																		}
																	}}
																	className={
																		styles.actionBtn
																	}
																>
																	<svg
																		xmlns="http://www.w3.org/2000/svg"
																		width="40"
																		height="40"
																		viewBox="0 0 40 40"
																		fill="none"
																	>
																		<rect
																			x="0.5"
																			y="0.5"
																			width="39"
																			height="39"
																			rx="4.5"
																			fill="white"
																		/>
																		<rect
																			x="0.5"
																			y="0.5"
																			width="39"
																			height="39"
																			rx="4.5"
																			stroke="#D9D9D9"
																		/>
																		<path
																			d="M18 19V25M22 19V25M27 14V28C27 28.5304 26.7893 29.0391 26.4142 29.4142C26.0391 29.7893 25.5304 30 25 30H15C14.4696 30 13.9609 29.7893 13.5858 29.4142C13.2107 29.0391 13 28.5304 13 28V14M11 14H29M16 14V12C16 11.4696 16.2107 10.9609 16.5858 10.5858C16.9609 10.2107 17.4696 10 18 10H22C22.5304 10 23.0391 10.2107 23.4142 10.5858C23.7893 10.9609 24 11.4696 24 12V14"
																			stroke="black"
																			stroke-width="1.5"
																			stroke-linecap="round"
																			stroke-linejoin="round"
																		/>
																	</svg>
																</button>
															</Tooltip>
														)}
													</>
												)}
											</>
										)}
									</div>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			{/* FOOTER ACTION GROUP FOR MOBILE */}
			<div className={styles.mobileFooterActions}>
				{onSecondaryAction && (
					<button
						className={styles.secondaryFooterBtn}
						onClick={onSecondaryAction}
					>
						{secondaryActionText}
					</button>
				)}

				{onAddResource && (
					<button
						className={styles.primaryFooterBtn}
						onClick={onAddResource}
					>
						{primaryActionText}{' '}
						<span className={styles.plus}>+</span>
					</button>
				)}
			</div>
		</div>
	);
};

export default ResourceManager;
